import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight, Clock, Calendar, Sparkles } from 'lucide-react';
import { fetchBlogs } from '../lib/api';

export default function BlogSection() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchBlogs()
      .then((data) => setPosts((data || []).slice(0, 3)))
      .catch(() => setPosts([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <section className="bg-[#f4f1eb] py-24 lg:py-32 relative overflow-hidden">
      <div className="absolute inset-0 bg-grid-light opacity-60" />
      <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-8 mb-14">
          <div>
            <div className="inline-flex items-center gap-2 rounded-full border border-amber-500/40 bg-amber-500/10 px-3 py-1 mb-4">
              <Sparkles className="h-3 w-3 text-amber-700" />
              <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-amber-800">
                AI-updated daily
              </span>
            </div>
            <h2 className="font-display text-4xl lg:text-5xl font-semibold text-[#0b0b0d] max-w-2xl leading-[1.05]">
              Sharpen your craft with practical welding know-how.
            </h2>
          </div>
          <Link
            to="/insights"
            className="group inline-flex items-center gap-2 font-medium text-[#0b0b0d] hover:text-amber-600 transition-colors"
          >
            View all articles
            <ArrowUpRight className="h-4 w-4 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
          </Link>
        </div>

        {loading ? (
          <div className="grid md:grid-cols-3 gap-6">
            {[1, 2, 3].map((i) => (
              <div key={i} className="rounded-2xl border border-neutral-200 bg-white overflow-hidden animate-pulse">
                <div className="aspect-[16/10] bg-neutral-200" />
                <div className="p-6 space-y-3">
                  <div className="h-4 bg-neutral-200 rounded w-3/4" />
                  <div className="h-4 bg-neutral-200 rounded w-1/2" />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="grid md:grid-cols-3 gap-6">
            {posts.map((p) => (
              <Link
                to={`/insights/${p.slug}`}
                key={p.slug}
                className="card-lift group bg-white rounded-2xl overflow-hidden border border-neutral-200 flex flex-col"
              >
                <div className="relative aspect-[16/10] overflow-hidden bg-[#0b0b0d]">
                  <img
                    src={p.image}
                    alt={p.title}
                    className="h-full w-full object-cover opacity-95 group-hover:scale-105 transition-transform duration-700"
                  />
                  <div className="absolute top-4 left-4 font-mono text-[10px] uppercase tracking-[0.18em] text-white/90 bg-white/10 backdrop-blur px-2.5 py-1 rounded">
                    {p.category}
                  </div>
                </div>
                <div className="p-6 flex-1 flex flex-col">
                  <h3 className="font-display text-xl font-semibold text-[#0b0b0d] leading-snug group-hover:text-amber-700 transition-colors">
                    {p.title}
                  </h3>
                  <p className="mt-3 text-sm text-neutral-600 leading-relaxed flex-1">
                    {p.excerpt}
                  </p>
                  <div className="mt-5 pt-4 border-t border-neutral-100 flex items-center justify-between text-[11px] text-neutral-500 font-mono uppercase tracking-wider">
                    <span className="inline-flex items-center gap-1.5">
                      <Calendar className="h-3 w-3" /> {p.date}
                    </span>
                    <span className="inline-flex items-center gap-1.5">
                      <Clock className="h-3 w-3" /> {p.read_time || p.readTime}
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
